/* eslint-disable @next/next/no-img-element */
import React, { useState, useCallback, useRef, useEffect } from "react";

interface ImageData {
  image: string;
  alt?: string;
  text?: string;
  type?: string;
  preview?: string;
}

interface SliderImageProps {
  data: ImageData[];
  width?: string;
  showDescription?: boolean;
  direction?: "left" | "right";
}

let cx = 0;
let cy = 0;
let lensCurrent: HTMLDivElement | null = null;
let imageCurrent: HTMLImageElement | null = null;
let resultCurrent: HTMLDivElement | null = null;

const SilderImage: React.FC<SliderImageProps> = ({
  data,
  width = "auto",
  showDescription = true,
  direction = "right",
}) => {
  const [index, setIndex] = useState(0);
  const selectedImage = data[index];
  const refImage = useRef<HTMLImageElement>(null);
  const refLens = useRef<HTMLDivElement>(null);
  const refResult = useRef<HTMLDivElement>(null);
  const [videoLoading, setVideoLoading] = useState(true);

  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  function getCursorPos(
    e: MouseEvent,
    imageCurrent: HTMLImageElement
  ): { x: number; y: number } {
    let x = 0;
    let y = 0;
    const getBoundingImage = imageCurrent.getBoundingClientRect();

    e = e || (window.event as MouseEvent);
    x = e.pageX - getBoundingImage.left - window.pageXOffset;
    y = e.pageY - getBoundingImage.top - window.pageYOffset;

    return { x, y };
  }

  useEffect(() => {
    if (selectedImage.type !== "image") return;

    lensCurrent = refLens.current;
    imageCurrent = refImage.current;
    resultCurrent = refResult.current;

    function moveLens(
      e: MouseEvent,
      imageWidth: number,
      imageHeight: number,
      offsetWidthLens: number,
      offsetHeightLens: number
    ) {
      e.preventDefault();
      let x = 0;
      let y = 0;

      const pos = getCursorPos(e, imageCurrent!);
      x = pos.x - offsetWidthLens / 2;
      y = pos.y - offsetHeightLens / 2;

      if (x > imageWidth - offsetWidthLens) {
        x = imageWidth - offsetWidthLens;
      }
      if (x < 0) {
        x = 0;
      }
      if (y > imageHeight - offsetHeightLens) {
        y = imageHeight - offsetHeightLens;
      }
      if (y < 0) {
        y = 0;
      }

      if (lensCurrent) {
        lensCurrent.style.left = `${x}px`;
        lensCurrent.style.top = `${y}px`;
      }

      if (resultCurrent) {
        resultCurrent.style.backgroundPosition = `-${x * cx}px -${y * cy}px`;
      }
    }

    const calculateZoomDimensions = () => {
      if (imageCurrent && lensCurrent && resultCurrent) {
        const imageWidth = imageCurrent.width;
        const imageHeight = imageCurrent.height || imageWidth - 120;
        const offsetWidthLens = lensCurrent.offsetWidth;
        const offsetHeightLens = lensCurrent.offsetHeight;

        cx = resultCurrent.offsetWidth / offsetWidthLens;
        cy = resultCurrent.offsetHeight / offsetHeightLens;

        resultCurrent.style.backgroundSize = `${imageWidth * cx}px ${
          imageHeight * cy
        }px`;

        imageCurrent.addEventListener("mousemove", (e) =>
          moveLens(
            e,
            imageWidth,
            imageHeight,
            offsetWidthLens,
            offsetHeightLens
          )
        );
        lensCurrent.addEventListener("mousemove", (e) =>
          moveLens(
            e,
            imageWidth,
            imageHeight,
            offsetWidthLens,
            offsetHeightLens
          )
        );
      }
    };

    if (imageCurrent) {
      if (imageCurrent.complete) {
        calculateZoomDimensions();
      } else {
        imageCurrent.onload = () => {
          calculateZoomDimensions();
        };
      }
    }
  }, [[selectedImage]]);

  const onPrevSlider = useCallback(() => {
    setIndex((prev) => (prev - 1 + data.length) % data.length);
  }, [data.length]);

  const onNextSlider = useCallback(() => {
    setIndex((prev) => (prev + 1) % data.length);
  }, [data.length]);

  return (
    <div className="react-slider" style={{ width: width || "auto" }}>
      <div className="react-slider__container">
        {selectedImage.type === "image" ? (
          <>
            {" "}
            <div className="react-slider__areaZoom">
              <div className="react-slider__lens" ref={refLens} />
              <div className="react-slider__picture">
                <img
                  src={selectedImage.image}
                  alt={selectedImage.alt}
                  ref={refImage}
                />
              </div>
              <div
                ref={refResult}
                className="react-slider__imgZoom"
                style={{
                  backgroundImage: `url(${selectedImage.image})`,
                  left: `${direction === "left" && "-107%"}`,
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="video-middle">
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                src={selectedImage.image}
                controls
                autoPlay
                muted
                playsInline
                loop
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  opacity: videoLoading ? 0.5 : 1,
                }}
                onCanPlayThrough={(e) => {
                  (e.currentTarget as HTMLVideoElement).style.background =
                    "none";
                }}
                onError={(e) => {
                  (e.currentTarget as HTMLVideoElement).poster =
                    "/fallback.jpg";
                }}
                onCanPlay={() => setVideoLoading(false)}
              />
            </div>
          </>
        )}
        <button
          type="button"
          className="react-slider__btnPrev"
          onClick={onPrevSlider}
        />
        <button
          type="button"
          className="react-slider__btnNext"
          onClick={onNextSlider}
        />
        {showDescription && (
          <div className="react-slider__description">{selectedImage.text}</div>
        )}
      </div>
      <ul className="react-slider__ul">
        {data.map((item, idx) => (
          <li
            key={idx}
            className={idx === index ? "active" : ""}
            onClick={() => {
              setIndex(idx);
            }}
          >
            <img
              src={item.type == "video" ? item.preview : item.image}
              alt={item.alt}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SilderImage;
